import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Box
} from '@mui/material';
import { useSnackbar } from 'notistack';
import axios from 'axios';

interface ChangeControlCenterPasswordDialogProps {
  open: boolean;
  onClose: () => void;
  children?: React.ReactNode;
}

const ChangeControlCenterPasswordDialog: React.FC<ChangeControlCenterPasswordDialogProps> = ({
  open,
  onClose,
  children
}) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // 입력값 검증
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('모든 필드를 입력');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('새 비밀번호와 확인 비밀번호가 일치하지 않음');
      return;
    }

    if (newPassword.length < 4) {
      setError('비밀번호는 4자 이상');
      return;
    }

    setLoading(true);

    try {
      // 로컬 스토리지에서 토큰 가져오기
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('인증 토큰 없음. 다시 로그인');
      }

      const response = await axios.put('/api/settings/control-center/password', 
        {
          currentPassword,
          newPassword
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        enqueueSnackbar('비밀번호가 변경 완료', { variant: 'success' });
        onClose();
        // 입력 필드 초기화
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      }
    } catch (error: any) {
      console.error('비밀번호 변경 오류:', error);
      if (error.response?.status === 401) {
        setError('인증 필요. 다시 로그인');
      } else if (error.response?.status === 403) {
        setError('권한 없음. 관리자 계정으로 로그인');
      } else if (error.response?.status === 400) {
        setError(error.response.data.detail || '현재 비밀번호가 올바르지 않음');
      } else {
        setError('비밀번호 변경 중 오류 발생');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      {children}
    </Dialog>
  );
};

export default ChangeControlCenterPasswordDialog; 